import { Box, Text, theme } from '@everlywell/ui-kit';
import React from 'react';

export type PriceV2Props = {
  /** Dollar amount (value before cents) */
  dollarAmount: number | string;
  /** Optional cents amount (value after dollar amount) */
  centsAmount?: number | string;
  /** Optional variant to display a strikethrough or discounted price. Normal is the default */
  variant?: 'normal' | 'strikethrough' | 'discountedPrice';
};

/**
 * Price V2 component
 * Used to display a price
 * Uses the Everlywell UI Kit components
 *
 * @param {PriceV2Props} props
 * @returns {JSX.Element}
 */
function PriceV2({
  dollarAmount,
  centsAmount,
  variant = 'normal',
}: PriceV2Props): JSX.Element {
  const textColor =
    variant === 'strikethrough'
      ? theme.colors.tints.darkGrey
      : variant === 'discountedPrice'
      ? theme.colors.utility.sale
      : theme.colors.tints.black;

  const hasStrikethrough = variant === 'strikethrough';
  const hasDiscountedPrice = variant === 'discountedPrice';

  return (
    <Box
      display="flex"
      alignItems="baseline"
      position="relative"
      width="fit-content"
      color={
        hasStrikethrough ? theme.colors.utility.sale : theme.colors.tints.black
      }
      role={hasStrikethrough ? 'deletion' : undefined}
      sx={
        hasStrikethrough
          ? {
              color: theme.colors.tints.darkGrey,
              position: 'relative',
              ':after': {
                position: 'absolute',
                left: '0',
                content: '""',
                display: 'block',
                background: theme.colors.utility.sale,
                height: '1.5px',
                width: '100%',
                bottom: '50%',
                transform: 'translateY(50%)',
              },
            }
          : {}
      }
    >
      <Text
        fontSize={hasDiscountedPrice ? ['md', 'lg'] : ['sm', 'md']}
        color={textColor}
        as="span"
        fontWeight={'medium'}
      >
        $
      </Text>
      <Text
        fontSize={hasDiscountedPrice ? ['lg', '2xl'] : ['lg', 'xl']}
        color={textColor}
        as="span"
        fontWeight={'medium'}
      >
        {dollarAmount}
      </Text>
      <Text
        fontSize={['sm', 'md']}
        color={textColor}
        as="span"
        fontWeight={'medium'}
      >
        {centsAmount && `.${centsAmount}`}
      </Text>
    </Box>
  );
}

export default PriceV2;
